import { useCallback, useEffect, useRef } from "react"
import { useSelector } from "react-redux"

import { IUser, selectUser } from "@/store/user/userSlice"

declare global {
  interface Window {
    chatwootSettings?: Record<string, unknown>
    chatwootSDK?: { run: (config: { websiteToken: string; baseUrl: string }) => void; cleanup?: () => void }
    $chatwoot?: {
      hasLoaded: boolean
      setUser: (email: string, user: { name: string; email: string }) => void
      reset: () => void
      /**
       * Opens/closes the chat window. Called with no argument it flips the
       * current state, which is what our own launcher wants: Chatwoot stays the
       * single owner of open/closed, so its own close control and ours agree.
       * Optional — older SDK builds may not expose it.
       */
      toggle?: (state?: "open" | "close") => void
    }
  }
}

const ALLOWED_CHATWOOT_HOSTS = ["app.chatwoot.com"]

function isAllowedChatwootUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === "https:" && ALLOWED_CHATWOOT_HOSTS.includes(parsed.hostname)
  } catch {
    return false
  }
}

const ChatwootWidget = () => {
  const websiteToken = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN
  const baseUrl = import.meta.env.VITE_CHATWOOT_BASE_URL

  const user = useSelector(selectUser)
  const prevUserRef = useRef<IUser | null>(null)
  const chatwootReadyRef = useRef(false)

  const syncUser = useCallback((currentUser: IUser | null, previousUser: IUser | null) => {
    if (!window.$chatwoot?.hasLoaded) return

    if (currentUser?.email) {
      window.$chatwoot.setUser(currentUser.email, {
        name: `${currentUser.firstName ?? ""} ${currentUser.lastName ?? ""}`.trim(),
        email: currentUser.email,
      })
    } else if (!currentUser?.email && previousUser?.email) {
      window.$chatwoot.reset()
    }
  }, [])

  useEffect(() => {
    if (!websiteToken || !baseUrl) return

    if (!isAllowedChatwootUrl(baseUrl)) {
      console.error("Chatwoot base URL is not in the allowed hosts list:", baseUrl)
      return
    }

    window.chatwootSettings = {
      position: "right",
      locale: "en",
      type: "standard",
      // Moogli owns the visible launcher: `SupportChatButton` sits in the app
      // header instead. Chatwoot still loads and works exactly as before — only
      // its own floating bottom-right bubble is hidden, so there is one way in
      // rather than two competing ones (MPE-1224).
      hideMessageBubble: true,
    }

    const onChatwootReady = () => {
      chatwootReadyRef.current = true
    }

    window.addEventListener("chatwoot:ready", onChatwootReady)

    const script = document.createElement("script")
    script.src = `${baseUrl}/packs/js/sdk.js`
    script.async = true
    script.onload = () => {
      window.chatwootSDK?.run({ websiteToken, baseUrl })
    }
    document.head.appendChild(script)

    return () => {
      window.removeEventListener("chatwoot:ready", onChatwootReady)
      chatwootReadyRef.current = false
      script.remove()
      window.chatwootSDK?.cleanup?.()
    }
  }, [websiteToken, baseUrl])

  useEffect(() => {
    if (chatwootReadyRef.current) {
      syncUser(user, prevUserRef.current)
    }
    prevUserRef.current = user
  }, [user, syncUser])

  return null
}

export default ChatwootWidget
